/**
 * GeocodingService - Handles location search and reverse geocoding
 *
 * Uses Nominatim (OpenStreetMap) as the free geocoding provider.
 * No API key required.
 *
 * Features:
 * - Forward geocoding (address/place name -> coordinates)
 * - Reverse geocoding (coordinates -> place name)
 * - Result caching (15-minute TTL) to minimize API calls
 * - Rate limiting (1 request/second) for Nominatim usage policy compliance
 */

import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('GeocodingService');

export interface GeocodingResult {
  lat: number;
  lng: number;
  displayName: string;
  shortName: string;
  address?: {
    city?: string;
    state?: string;
    country?: string;
  };
}

export interface SearchSuggestion {
  id: string;
  displayName: string;
  shortName: string;
  lat: number;
  lng: number;
  type: string;
}

interface CacheEntry<T> {
  result: T;
  timestamp: number;
}

// Nominatim API response types
interface NominatimSearchResult {
  place_id: string;
  display_name: string;
  name?: string;
  lat: string;
  lon: string;
  type?: string;
}

interface NominatimReverseResponse {
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  error?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
}

export class GeocodingService {
  private static instance: GeocodingService;
  private cache: Map<string, CacheEntry<unknown>> = new Map();

  private static readonly CACHE_TTL = 15 * 60 * 1000; // 15 minutes
  private static readonly NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
  private static readonly RATE_LIMIT_MS = 1000; // 1 second between requests

  private lastRequestTime = 0;

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): GeocodingService {
    if (!GeocodingService.instance) {
      GeocodingService.instance = new GeocodingService();
    }
    return GeocodingService.instance;
  }

  /**
   * Search for locations by name/address
   * @param query - Search query (address, place name, etc.)
   * @param limit - Maximum number of results (default 5)
   * @returns Array of search suggestions
   */
  async search(query: string, limit = 5): Promise<SearchSuggestion[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = `search:${normalizedQuery}:${limit}`;
    const cached = this.getFromCache<SearchSuggestion[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        limit: String(limit),
        addressdetails: '1',
      });

      const url = `${GeocodingService.NOMINATIM_BASE}/search?${params}`;
      const data = (await this.fetchWithRateLimit(url)) as NominatimSearchResult[] | null;

      if (!Array.isArray(data)) {
        return [];
      }

      const results: SearchSuggestion[] = data.map((item: NominatimSearchResult) => ({
          id: String(item.place_id),
          displayName: item.display_name,
          shortName: item.name || item.display_name.split(',')[0].trim(),
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          type: item.type || 'place',
        })
      );

      this.setCache(cacheKey, results);
      return results;
    } catch (error) {
      logger.warn('Search failed:', error);
      return [];
    }
  }

  /**
   * Get place name from coordinates (reverse geocoding)
   * @param lat - Latitude
   * @param lng - Longitude
   * @returns Geocoding result with place name, or null if failed
   */
  async reverseGeocode(lat: number, lng: number): Promise<GeocodingResult | null> {
    // Round to 5 decimal places for cache key (about 1 meter precision)
    const roundedLat = lat.toFixed(5);
    const roundedLng = lng.toFixed(5);
    const cacheKey = `reverse:${roundedLat},${roundedLng}`;

    const cached = this.getFromCache<GeocodingResult>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const params = new URLSearchParams({
        lat: String(lat),
        lon: String(lng),
        format: 'json',
        addressdetails: '1',
      });

      const url = `${GeocodingService.NOMINATIM_BASE}/reverse?${params}`;
      const data = (await this.fetchWithRateLimit(url)) as NominatimReverseResponse | null;

      if (!data || data.error) {
        return null;
      }

      const result: GeocodingResult = {
        lat: parseFloat(data.lat),
        lng: parseFloat(data.lon),
        displayName: data.display_name,
        shortName: data.name || data.display_name?.split(',')[0]?.trim() || '',
        address: data.address
          ? {
              city: data.address.city || data.address.town || data.address.village,
              state: data.address.state,
              country: data.address.country,
            }
          : undefined,
      };

      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      logger.warn('Reverse geocode failed:', error);
      return null;
    }
  }

  /**
   * Fetch with rate limiting to comply with Nominatim usage policy
   */
  private async fetchWithRateLimit(url: string): Promise<unknown> {
    // Enforce rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;

    if (timeSinceLastRequest < GeocodingService.RATE_LIMIT_MS) {
      const delay = GeocodingService.RATE_LIMIT_MS - timeSinceLastRequest;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    this.lastRequestTime = Date.now();

    const response = await fetch(url, {
      headers: {
        'User-Agent': 'WebXRGallery/1.0 (https://xrgallery.com)',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < GeocodingService.CACHE_TTL) {
      return cached.result as T;
    }
    // Clean up expired entry
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  private setCache<T>(key: string, result: T): void {
    this.cache.set(key, { result, timestamp: Date.now() });

    // Clean up old entries if cache gets too large (>100 entries)
    if (this.cache.size > 100) {
      const now = Date.now();
      for (const [k, v] of this.cache.entries()) {
        if (now - v.timestamp > GeocodingService.CACHE_TTL) {
          this.cache.delete(k);
        }
      }
    }
  }

  /**
   * Clear all cached results
   */
  clearCache(): void {
    this.cache.clear();
  }
}
